import { LatLng, Point2D, Geodetic, Cartesian, Coordinate  } from './map-types';

export function toRadians(degrees: number): number {
  return Math.PI * degrees / 180;
}

export function toDegrees(radians: number): number {
  return 180 * radians / Math.PI;
}

export class Mercator {
            public static fromLatLngToImagePoint(latLng: Geodetic): Point2D {
                return Mercator.fromLatLngToTransformedPoint(latLng, -0.5, 0.5, true); //returns the point in image coordinate direction (Origin at top-left and positive y is downward
            }
            public static fromLatLngToTransformedPoint(latLng: Geodetic, Ox: number, Oy: number, flipY: boolean): Point2D {
                let p: Cartesian = Mercator.fromLatLngToPoint(latLng);
                let flip: number = flipY ? 1.0 : -1.0;
                return new Point2D((p.x - Ox), (flip * Oy + flip * -p.y));
            }
            public static fromLatLngToPoint(latLng: Geodetic, resolution?:number): Point2D {
                return <Point2D>Mercator.fromLatLngToAny(latLng,true,resolution);
            }
            public static fromLatLngToPointArray(latLng: Geodetic, resolution?:number): Coordinate {
                return <Coordinate>Mercator.fromLatLngToAny(latLng,false,resolution);
            }
            
            private static fromLatLngToAny(latLng: Geodetic, asPoint2D:boolean ,resolution:number = 1): Coordinate | Point2D {
                    let sinLat: number = Math.sin(toRadians(latLng.lat));
                    let nX: number = (latLng.lng / 360.0);
                    let nY: number = ((Math.log((1.0 + sinLat) / (1.0 - sinLat)) / (4.0 * Math.PI)));
                    return asPoint2D ? new Point2D(nX*resolution, nY*resolution) : [ nX*resolution, nY*resolution ];
                }


            public static fromImagePointToLatLng(point: Cartesian): Geodetic {
                return Mercator.fromTransformedPointToLatLng(point, -0.5, 0.5, true); //the point in image coordinate direction (Origin at top-left and positive y is downward
            }
            public static fromTransformedPointToLatLng(point: Cartesian, Ox: number, Oy: number, flipY: boolean): LatLng {
                let flip: number = flipY ? 1.0 : -1.0;
                let nX: number = point.x + Ox;
                let nY: number = (flip * Oy - point.y) / flip
                return Mercator.fromArrayToLatLng([nX, nY]);
            }
            public static fromPointToLatLng(point: Cartesian, resolution:number = 1): LatLng {
                let lng: number = (point.x/resolution) * 360.0;
                let lat: number = toDegrees(2.0 * Math.atan(Math.exp((point.y/resolution) * 2.0 * Math.PI)) - Math.PI / 2.0);
                return new LatLng(lat, lng);
            }
            public static fromArrayToLatLng(point: number[], resolution:number = 1): LatLng {
                let lng: number = (point[0]/resolution) * 360.0;
                let lat: number = toDegrees(2.0 * Math.atan(Math.exp((point[1]/resolution) * 2.0 * Math.PI)) - Math.PI / 2.0);
                return new LatLng(lat, lng);
            }

}

        /**
         * https://en.wikipedia.org/wiki/Transverse_Mercator:_Redfearn_series
         * @author marc
         */
export class Datum {
            private static readonly _2F: number = 2.0; //2!
            private static readonly _3F: number = Datum._2F * 3.0; //3!
            private static readonly _4F: number = Datum._3F * 4.0; //4!
            private static readonly _5F: number = Datum._4F * 5.0; //5!
            private static readonly _6F: number = Datum._5F * 6.0; //6!
            private static readonly _7F: number = Datum._6F * 7.0; //7!
            private static readonly _8F: number = Datum._7F * 8.0; //8!


            private b: number; //radius at the poles
            private f3: number; //third flattening
            private n: number; //f3 is sometimes referenced as n: n = f3

            private e: number; //(first) eccentricity

            //useful intermediate results that appear all over the place
            private E: number; // e^2

            //coefficients used for calculating the meridian distance from the equator to a point at latitude phi 
            private B0: number;
            private B2: number;
            private B4: number;
            private B6: number;


            //coefficients used for calculating the geodetic latitude of a foot point 
            private D2: number;
            private D4: number;
            private D6: number;
            private D8: number;


            //variables that will hold intermediate results that appear all over the place based on the lat and lng (so these need to be recalculated for every new location)
            private phi0: number; //latitude of the origin in radians
            private zcm: number; //longitude of the origin (central meridian)

            private m0: number; //meridian distance from the equator to a point at latitude phi0

            public static get(name: string, a: number, inv_f: number, k0: number): Datum {
                return new Datum(name, a, Number.isFinite(inv_f) ? 1.0 / inv_f : 0.0, k0);
            }

            /**
             * Constructs a geodetic coordinate system (geodetic datum) for an ellipsoid with given parameters
             * @param name used for logging purposes
             * @param a radius at equator
             * @param f (first) flattening
             * @param k0 scale at central merdidian
             */
            private constructor(public name: string, private a: number, private f: number, private k0: number) {
                //the basics
                this.b = this.a * (1 - this.f);
                this.f3 = (this.a - this.b) / (this.a + this.b);
                this.n = this.f3; //f3 is sometimes referenced as n: n = f3

                this.e = Math.sqrt(1.0 - (this.b * this.b) / (this.a * this.a));
                this.E = this.e * this.e;

                let np2: number = this.n * this.n,
                    np3: number = np2 * this.n,
                    np4: number = np3 * this.n;

                this.B0 = this.b * (1 + this.n + np2 * 5.0 / 4.0 + np3 * 5.0 / 4.0);
                this.B2 = -this.b * (this.n * 3.0 / 2.0 + np2 * 3.0 / 2.0 + np3 * 21.0 / 16.0);
                this.B4 = this.b * (np2 * 15.0 / 16.0 + np3 * 15.0 / 16.0);
                this.B6 = -this.b * (np3 * 35.0 / 48.0);

                this.D2 = this.n * 3.0 / 2.0 - np3 * 27.0 / 32.0;
                this.D4 = np2 * 21.0 / 16.0 - np4 * 55.0 / 32.0;
                this.D6 = np3 * 151.0 / 96.0;
                this.D8 = np4 * 1097.0 / 512.0;

                this.phi0 = null;
            }
            public toString(): string {
                return `${this.name}(a:${this.a.toFixed(1)}, f:${this.f.toFixed(6)}, ${this.k0.toFixed(6)}`;
            }
            public radius(): number {
                return this.a;
            }
            /**
             * Sets the origin of the Cartesian coordinate space to given longitude, latitude. This
             * method need to be called before you can start making (multiple) calls to direct()
             * and inverse().
             * @param latLng0 the geodetic latitude and longitude of the origin in degrees
             * @return the datum for consecutive calls
             */
            public origin(latLng0: Geodetic): Datum {
                this.zcm = latLng0.lng; //the central meridian
                this.phi0 = toRadians(latLng0.lat); //lat in radians
                this.m0 = this._m(this.phi0);
                return this;
            }
            /**
             * Returns the x,y coordinate for given longitude and latitude. A call to origin() needs
             * to be made first before you can call this method.
             * @param latLng the geodetic latitude and longitude in degrees
             * @return the Cartesian coordinates [x,y] of given location in metres from the origin. 
             */
            public direct(latLng: Geodetic): Point2D {
                if (this.phi0 == null) throw new Error("The origin has not been set.");

                let phi: number = toRadians(latLng.lat); //latitude in radians
                let lambda: number = toRadians(latLng.lng - this.zcm); //the delta between longitude and zcm in radians

                let s: number = Math.sin(phi); //sin(phi)
                let c: number = Math.cos(phi); //cos(phi)
                let t: number = Math.tan(phi); //tan(phi)

                let S: number = s * s; //sin(phi)^2
                let T: number = t * t; //tan(phi)^2

                let v: number = this._v(S); //the radius of curvature of the meridian at latitude phi
                let p: number = this._p(v); //the radius of curvature in the prime vertical at latitude phi
                let B: number = v / p; //the ratio v:p at latitude phi;

                let m: number = this._m(phi); //meridian distance from the equator to a point at latitude phi

                let lc: number = lambda * c;
                let lcp2: number = lc * lc,
                    lcp4: number = lcp2 * lcp2,
                    lcp6: number = lcp4 * lcp2,
                    lcp8: number = lcp6 * lcp2;

                let vt: number = v * t;

                let Bp2: number = B * B,
                    Bp3: number = Bp2 * B,
                    Bp4: number = Bp3 * B;

                let Tp2: number = T * T, //is tan(phi)^4
                    Tp3: number = Tp2 * T; //is tan(phi)^6

                let W3: number = B - T;
                let W5: number = 4.0 * Bp3 * (1.0 - 6.0 * T) + Bp2 * (1.0 + 8.0 * T) - 2.0 * B * T + Tp2;
                let W7: number = 61.0 - 479.0 * T + 179.0 * Tp2 - Tp3; // + O(E) -> no idea : it refers to a concept called 'big O' in mathematics

                let W4: number = 4.0 * Bp2 + B - T;
                let W6: number = 8.0 * Bp4 * (11.0 - 24.0 * T) - 28.0 * Bp3 * (1.0 - 6.0 * T) + Bp2 * (1.0 - 32.0 * T) - 2.0 * B * T + Tp2;
                let W8: number = 1385.0 - 3111.0 * T + 543.0 * Tp2 - Tp3; // + O(E) -> no idea : it refers to a concept called 'big O' in mathematics

                let x: number = this.k0 * v * lc * (1.0 + lcp2 * W3 / Datum._3F + lcp4 * W5 / Datum._5F + lcp6 * W7 / Datum._7F);
                let y: number = this.k0 * (m - this.m0 + lcp2 * vt / Datum._2F + lcp4 * vt * W4 / Datum._4F + lcp6 * vt * W6 / Datum._6F + lcp8 * vt * W8 / Datum._8F);

                return new Point2D(x, y);
            }
            /**
             * Returns the longitude,latitude coordinate for given x and y. A calll to origin() needs
             * to be made first before you can call this method.
             * @param latLng the geodetic latitude and longitude in degrees
             * @return the geodetic [longitude,latitude] of given location.
             */
            public inverse(point: Cartesian): LatLng {
                if (this.phi0 == null) throw new Error("The origin has not been set.");

                let m1: number = this.m0 + point.y / this.k0; //meridian distance from the equator to the footpoint. foot point is point (0,y)  
                let mu: number = m1 / this.B0;

                let phi1: number = mu + this.D2 * Math.sin(2.0 * mu) + this.D4 * Math.sin(4.0 * mu) + this.D6 * Math.sin(6.0 * mu) + this.D8 * Math.sin(8.0 * mu);
                let s1: number = Math.sin(phi1);
                let c1: number = Math.cos(phi1);
                let t1: number = Math.tan(phi1);
                let S1: number = s1 * s1;
                let T1: number = t1 * t1;
                let v1: number = this._v(S1);
                let p1: number = this._p(v1);
                let b1: number = v1 / p1;
                let b1t1: number = t1 * b1;

                let T1p2: number = T1 * T1, //is tan(phi1)^4
                    T1p3: number = T1p2 * T1; //is tan(phi1)^6
                let b1p2: number = b1 * b1,
                    b1p3: number = b1p2 * b1,
                    b1p4: number = b1p3 * b1;
                let k0v1: number = this.k0 * v1,
                    k0v1p2: number = k0v1 * k0v1,
                    k0v1p3: number = k0v1p2 * k0v1,
                    k0v1p4: number = k0v1p3 * k0v1,
                    k0v1p5: number = k0v1p4 * k0v1,
                    k0v1p6: number = k0v1p5 * k0v1,
                    k0v1p7: number = k0v1p6 * k0v1,
                    k0v1p8: number = k0v1p7 * k0v1;
                let xp2: number = point.x * point.x,
                    xp3: number = xp2 * point.x,
                    xp4: number = xp3 * point.x,
                    xp5: number = xp4 * point.x,
                    xp6: number = xp5 * point.x,
                    xp7: number = xp6 * point.x,
                    xp8: number = xp7 * point.x;

                let V3: number = b1 + 2.0 * T1;
                let V5: number = 4.0 * b1p3 * (1.0 - 6.0 * T1) - b1p2 * (9.0 - 68.0 * T1) - 72.0 * b1 * T1 - 24.0 * T1p2;;
                let V7: number = 61.0 + 662.0 * T1 + 1320.0 * T1p2 + 720.0 * T1p3;

                let U4: number = 4.0 * b1p2 - 9.0 * b1 * (1.0 - T1) - 12.0 * T1;;
                let U6: number = 8.0 * b1p4 * (11.0 - 24.0 * T1) - 12.0 * b1p3 * (21.0 - 71.0 * T1) + 15.0 * b1p2 * (15.0 - 98.0 * T1 + 15.0 * T1p2) + 180.0 * b1 * (5.0 * T1 - 3.0 * T1p2) + 360.0 * T1p2;
                let U8: number = -1385.0 - 3633.0 * T1 - 4095.0 * T1p2 - 1575.0 * T1p3;

                let lambda: number = point.x / (c1 * k0v1) - xp3 * V3 / (Datum._3F * c1 * k0v1p3) - xp5 * V5 / (Datum._5F * c1 * k0v1p5) - xp7 * V7 / (Datum._7F * c1 * k0v1p7);
                let phi: number = phi1 - xp2 * b1t1 / (Datum._2F * k0v1p2) - xp4 * b1t1 * U4 / (Datum._4F * k0v1p4) - xp6 * b1t1 * U6 / (Datum._6F * k0v1p6) - xp8 * b1t1 * U8 / (Datum._8F * k0v1p8);

                return new LatLng(toDegrees(phi), this.zcm + toDegrees(lambda));
            }
            /**
             * Returns the longitude,latitude coordinate for given x and y. A calll to origin() needs
             * to be made first before you can call this method.
             * @param x coordinate in metres from the origin
             * @param y coordinate in metres from the origin
             * @return the geodetic [longitude,latitude] of given location. 
             */

            /**
             * Calculates the radius of curvature of the meridian at latitude phi
             * @param S sin(phi)^2
             * @return v(phi)
             */
            private _v(S: number): number {
                return this.a / Math.sqrt(1.0 - this.E * S);
            }

            /**
             * Calculates the radius of curvature in the prime vertical at latitude phi
             * @param v radius of curvature of the meridian at latitude phi
             * @return p(phi)
             */
            private _p(v: number): number {
                return v * v * v * (1.0 - this.E) / (this.a * this.a);
            }
            /**
             * Calculates the meridian distance from the equator to a point at latitude phi
             * @param phi the geodetic latitude in radians
             * @return the meridian distance in metres
             */
            private _m(phi: number): number {
                return this.B0 * phi + this.B2 * Math.sin(2.0 * phi) + this.B4 * Math.sin(4.0 * phi) + this.B6 * Math.sin(6.0 * phi);
            }

}

export class TransverseMercator {
            /**
             * Returns the default datum
             * @return the default datum (WGS 84)
             */
            public static getDefaultDatum(): Datum {
                return TransverseMercator.DATUMS[TransverseMercator.DATUM_WGS_84]();
            }
            public static getDefaultDatumIndex(): number {
                return TransverseMercator.DATUM_WGS_84;
            }
            /**
             * Returns one of the predefined datums
             * @param datumIndex
             * @return the predefined datum
             */
            public static getDatum(datumIndex: number): Datum {
                return TransverseMercator.DATUMS[datumIndex]();
            }
            /**
             * Returns  a geodetic coordinate system (geodetic datum) for an ellipsoid with given parameters.
             * @param a radius at equator
             * @param inv_f inverse flattening
             * @param k0 scale at central merdidian
             */
            public static getCustomDatum(a: number, inv_f: number, k0: number): Datum {
                return Datum.get(`Datum(a:${a.toFixed(1)}, f:${(1.0/inv_f).toFixed(6)}, ${k0.toFixed(6)}`, a, inv_f, k0);
            }

            public static readonly DATUM_SPHERE: number = 0;
            public static readonly DATUM_WGS_84: number = 1;
            public static readonly DATUM_NAD_83: number = 2;
            public static readonly DATUM_GRS_80: number = 3;
            public static readonly DATUM_WGS_72: number = 4;
            public static readonly DATUM_Australian_1965: number = 5;
            public static readonly DATUM_Krasovsky_1940: number = 6;
            public static readonly DATUM_North_American_1927: number = 7;
            public static readonly DATUM_International_1924: number = 8;
            public static readonly DATUM_Hayford_1909: number = 9;
            public static readonly DATUM_Clarke_1880: number = 10;
            public static readonly DATUM_Clarke_1866: number = 11;
            public static readonly DATUM_Airy_1830: number = 12;
            public static readonly DATUM_Bessel_1841: number = 13;
            public static readonly DATUM_Everest_1830: number = 14;


            private static readonly DATUMS: (() => Datum)[] = [
                () => Datum.get("Sphere", 6378137.0, Number.POSITIVE_INFINITY, 1.0),
                () => Datum.get("WGS 84", 6378137.0, 298.257223563, 0.9996),
                () => Datum.get("NAD 83", 6378137.0, 298.2572236, 0.9996),
                () => Datum.get("GRS 80", 6378137.0, 298.2572215, 0.9996),
                () => Datum.get("WGS 72", 6378135.0, 298.2597208, 0.9996),
                () => Datum.get("Australian 1965", 6378160.0, 298.2497323, 0.9996),
                () => Datum.get("Krasovsky 1940", 6378245.0, 298.2997381, 0.9996),
                () => Datum.get("North American 1927", 6378206.4, 294.9786982, 0.9996),
                () => Datum.get("International 1924", 6378388.0, 296.9993621, 0.9996),
                () => Datum.get("Hayford 1909", 6378388.0, 296.9993621, 0.9996),
                () => Datum.get("Clarke 1880", 6378249.1, 293.4660167, 0.9996),
                () => Datum.get("Clarke 1866", 6378206.4, 294.9786982, 0.9996),
                () => Datum.get("Airy 1830", 6377563.4, 299.3247788, 0.9996),
                () => Datum.get("Bessel 1841", 6377397.2, 299.1527052, 0.9996),
                () => Datum.get("Everest 1830", 6377276.3, 300.8021499, 0.9996)
            ];

        }
/**
 * see https://en.wikipedia.org/wiki/Universal_Transverse_Mercator_coordinate_system
 */        
export enum Hemisphere {
    North = 'N',
    South = 'S'
}
export class UTM {
    public static HEMISPHERE_NORTH: string = 'N';
    public static HEMISPHERE_SOUTH: string = 'S';
    
    private _datum:Datum;
    private _datumIndex:number;
    private _zone:number;
    private _hemisphere:string;
    private fromUTM: (xy: Coordinate) => Cartesian;
    private toUTM: (position: Cartesian) => Coordinate;
    
    public static get(zone:number, hemisphere:string|number, datumIndex?:number): UTM {
      
        let utm = new UTM();
        if(datumIndex!==undefined){
            const datum = TransverseMercator.getDatum(datumIndex);
            if(datum){
                utm._datumIndex = datumIndex;
                utm._datum = datum;
            }
        }
        if( (<string>hemisphere).substring ){
          utm.setZone( zone, <string>hemisphere );
        } else {
          //zone is latitude
          //hemisphere is longitude
          utm.setZone( UTM.getZone( zone, <number>hemisphere), UTM.getHemisphere( zone, <number>hemisphere) );
        }
        
        return utm;
    }
    public static getZone(lat:number, lng:number): number {
      return Math.floor(((lng + 180.0) / 6.0) + 1.0);
    }
    public static getHemisphere(lat:number, lng:number) : Hemisphere {
      return lat < 0.0 ? Hemisphere.South : Hemisphere.North;
    }

    private constructor(){
        this._datumIndex = TransverseMercator.getDefaultDatumIndex();
        this._datum = TransverseMercator.getDatum(this._datumIndex);
    }
    public datum(): Datum {
        return this._datum;
    }
    public datumIndex(): number {
        return this._datumIndex;
    }
    public zone(): number {
        return this._zone;
    }
    public hemisphere(): string {
        return this._hemisphere;
    }
    public setZone(zone: number, hemisphere:string) : Geodetic{
        this._zone = zone;
        this._hemisphere = hemisphere;
        let origin  = new LatLng( 0, (zone * 6) - 183 );
        this._datum.origin(origin);
        this.fromUTM = (xy: Coordinate) => new Point2D(xy[0]-500000,xy[1]);
        this.toUTM = (position: Cartesian) => [ position.x+500000, position.y ];
        if(hemisphere !== UTM.HEMISPHERE_NORTH ){
//            origin = this.datum.inverse(new Point2D(0,-10000000));
            this._datum.origin(origin);
            this.fromUTM = (xy: Coordinate) => new Point2D(xy[0]-500000,xy[1]-10000000);
            this.toUTM = (position: Cartesian) => [ position.x+500000, position.y+10000000 ];
        }
          
        return origin;
    }
    
    public toGeodetic(xy:Coordinate) : LatLng {
      return this._datum.inverse( this.fromUTM(xy) );
    }
    
    public toCartesian(lat:number, lng:number) : Coordinate {
        return this.toUTM( this._datum.direct(new LatLng (lat, lng) ) );
      }
}
